import * as Sentry from '@sentry/react';

/**
 * Reads a 0..1 sample rate from a build-time env var, clamped into range.
 *
 * Deliberately strict: `Number` rather than `parseFloat`, so a malformed repo
 * variable (`0.2x`, `20%`) falls back to the documented default instead of
 * silently becoming `0.2`. A typo'd dial that reverts to the default is
 * debuggable; one that half-parses is not.
 */
export function parseSampleRate(raw: string | undefined, fallback: number): number {
  const trimmed = raw?.trim();
  if (!trimmed) return fallback;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

/** The browser's own text for a cancelled fetch, nothing more and nothing less. */
const BROWSER_ABORT = /^(?:Error: )?(?:AbortError: )?The operation was aborted\.?$/;

/**
 * True for an unhandled rejection that is just the browser cancelling a
 * request (navigation away, a query unmounting mid-flight): no stack, no
 * first-party frame, and the exact text the browser gives.
 *
 * Deliberately narrow, so a first-party bug can never be swallowed by it:
 * - only `onunhandledrejection` events;
 * - EVERY entry in the exception chain must be that exact message (a real error
 *   wrapping or wrapped by an abort keeps the event);
 * - no entry may carry a stack frame (an abort thrown from our own code has one).
 */
export function isBenignAbort(event: Sentry.ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  return values.every((exception) => {
    if (exception.mechanism?.type !== 'auto.browser.global_handlers.onunhandledrejection') {
      return false;
    }
    if (exception.stacktrace?.frames?.length) return false;
    const value = typeof exception.value === 'string' ? exception.value.trim() : '';
    return BROWSER_ABORT.test(value);
  });
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // v11 collects request data by default; this keeps the restrictive v10 default.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      },
      httpBodies: [],
      urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replays are recorded unmasked so a session is actually readable: page
      // text and media come through as the user saw them. Note what this does
      // NOT switch off — the SDK's `maskAllInputs` still defaults to true, so
      // values typed into inputs/textareas stay redacted, and fields with
      // autocomplete="current-password|new-password|cc-*" are masked
      // unconditionally. To hide a specific element (a bank detail, an API key),
      // add `data-sentry-mask` / class `sentry-mask` to it rather than turning
      // masking back on globally.
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    beforeSend: (event) => (isBenignAbort(event) ? null : event),
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),
    // Record every dashboard session, not just the ones that end in an error.
    // Dial down per environment with VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE
    // (e.g. `0.2`) if replay quota becomes the constraint — no code change needed.
    replaysSessionSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      1.0,
    ),
    replaysOnErrorSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1.0,
    ),
  });
}

/**
 * Attaches the signed-in user to Sentry events and replays, so a replay can be
 * traced back to a person. Pass `null` on sign-out to clear it — otherwise the
 * next session on a shared browser inherits the previous identity.
 *
 * The tenant tag is owned by `setSentryTenant`, not this function — see the note
 * there for why the JWT is the wrong source for it.
 *
 * Safe to call before/without `initSentry()`: with no DSN the SDK is a no-op.
 */
export function identifySentryUser(
  user: { sub: string; email?: string; name?: string; preferred_username?: string } | null,
): void {
  if (!user) {
    Sentry.setUser(null);
    setSentryTenant(null);
    return;
  }

  Sentry.setUser({
    id: user.sub,
    email: user.email,
    // Tokens populate one or the other depending on the identity source.
    username: user.name ?? user.preferred_username,
  });
}

/**
 * Sets the `tenantId` tag — searchable/filterable in the replay list, which the
 * Sentry user object alone is not.
 *
 * This is driven by `TenantContext`'s active tenant rather than the JWT claim,
 * because the two diverge: `selectTenant` switches tenant with a client-side
 * `navigate` and no token refresh, so the JWT keeps the *previous* tenantId for
 * the rest of the session. Tagging from the token there would silently
 * mis-attribute every later event and replay to the tenant the user just left.
 * (`switchTenant` reloads the page, so it would have been fine either way.)
 *
 * One writer only — do not also set this tag from auth state, or a token refresh
 * will race the active tenant and clobber it.
 */
export function setSentryTenant(tenantId: string | null): void {
  Sentry.setTag('tenantId', tenantId ?? undefined);
}
